import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { FileText, Edit3, Save, X, Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ScreeningNotesProps {
  callRecordingId: string;
  initialNotes?: string;
}

export const ScreeningNotes = ({ callRecordingId, initialNotes }: ScreeningNotesProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [notes, setNotes] = useState(initialNotes || "");
  const [editedNotes, setEditedNotes] = useState(initialNotes || "");
  const [isUpdating, setIsUpdating] = useState(false);

  const handleEdit = () => {
    setEditedNotes(notes);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedNotes(notes);
  };

  const handleSave = async () => {
    setIsUpdating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Update call_recordings table with notes
      const { error } = await supabase
        .from('call_recordings')
        .update({ notes: editedNotes })
        .eq('id', callRecordingId);

      if (error) throw error;

      setNotes(editedNotes);
      setIsEditing(false);
      toast.success('Notes saved successfully');
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Failed to save notes');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-xl">
          <FileText className="h-5 w-5 text-primary" />
          Screening Notes
        </CardTitle>
        {!isEditing && (
          <Button variant="outline" size="sm" onClick={handleEdit}>
            {notes ? <Edit3 className="h-4 w-4 mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            {notes ? "Edit Notes" : "Add Notes"}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="space-y-4">
            <Textarea
              placeholder="Enter your screening notes here..."
              value={editedNotes}
              onChange={(e) => setEditedNotes(e.target.value)}
              className="min-h-[120px]"
              disabled={isUpdating}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={handleCancel} disabled={isUpdating}>
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={isUpdating}>
                <Save className="h-4 w-4 mr-1" /> {isUpdating ? "Saving..." : "Save Notes"}
              </Button>
            </div>
          </div>
        ) : (
          notes ? (
            <p className="whitespace-pre-wrap text-muted-foreground">{notes}</p>
          ) : (
            <p className="text-muted-foreground italic">No notes added yet.</p>
          )
        )}
      </CardContent>
    </Card>
  );
};